import {
  Dispatch,
  SetStateAction,
  useCallback,
  useLayoutEffect,
  useMemo,
  useState,
  useEffect,
} from "react";
import { app, dialog, HostClientType } from "@microsoft/teams-js";
import {
  teamsLightTheme,
  teamsDarkTheme,
  teamsHighContrastTheme,
  Theme,
} from "@fluentui/react-components";
import { TeamsUserCredential, UserInfo } from "@microsoft/teamsfx";
import { logger } from "../Logger";
import config from "../config/config";

/**
 * Returns the context of our teams application, with current theme, user information.
 *
 */
export type TeamsContextType = {
  theme: Theme;
  themeString: string;
  context: app.Context | undefined;
  teamsUserCredential: TeamsUserCredential;
  userInfo: UserInfo | undefined;
  clientType: HostClientType | undefined;
};

export const useTeamsContext = (): TeamsContextType => {
  const [theme, setTheme] = useState<Theme>(teamsLightTheme);
  const [themeString, setThemeString] = useState<string>("default");
  const [context, setContext] = useState<app.Context | undefined>(undefined);
  const [userInfo, setUserInfo] = useState<UserInfo | undefined>(undefined);
  const [clientType, setClientType] = useState<HostClientType | undefined>(
    undefined
  );

  logger.logTrace("Entered useTeamsContext");

  // Create an instance of teamsUserCredential
  const teamsUserCredential = useMemo(() => {
    const authConfig = {
      initiateLoginEndpoint: config.initiateLoginEndpoint,
      clientId: config.clientId,
    };

    return new TeamsUserCredential(authConfig);
  }, []);

  // handler to change theme
  const themeChangeHandler = useCallback((theme: string | undefined) => {
    setThemeString(theme || "default");
    switch (theme) {
      case "dark":
        setTheme(teamsDarkTheme);
        break;
      case "contrast":
        setTheme(teamsHighContrastTheme);
        break;
      case "default":
      default:
        setTheme(teamsLightTheme);
    }
  }, []);

  // subscribing to app theme change handler
  const subscribeAsync = useCallback(async () => {
    await app.initialize();
    const context = await app.getContext();
    themeChangeHandler(context.app.theme);
    setClientType(context.app.host.clientType);
    const userInfo = await teamsUserCredential.getUserInfo();
    logger.setTenantId(userInfo.tenantId);
    setUserInfo(userInfo);
    setContext(context);
    app.registerOnThemeChangeHandler(themeChangeHandler);
  }, [teamsUserCredential, themeChangeHandler]);

  // subscribing during layout effect
  useLayoutEffect(() => {
    (async () => {
      await subscribeAsync();
    })();
  }, [subscribeAsync]);

  logger.logTrace("Exit useTeamsContext");

  return {
    theme,
    themeString,
    context,
    teamsUserCredential,
    userInfo,
    clientType,
  };
};
